import { Fragment, useEffect, useRef, useState } from "react";

import { Button, Checkbox, Drawer, Textarea } from "@/components";
import { ArrowDown, InfoCircle, Minus, Plus } from "@/components/Icons";
import styles from "@/styles/containers/product-details.module.scss";

import { number, object } from "yup";

import { CatalogHelpers, Currency, OMath } from "@/tools";

import { DeepCopy } from "@/tools/DeepCopy";
import classNames from "classnames/bind";
import { useLocale, useTranslations } from "next-intl";
import { QuestionItemEcletica } from "./Step/QuestionItemEcletica";
import { OrderStorage } from "@/storage/OrderStorage";
import { TextWithLineBreaks } from "@/components/TextWithLineBreaks";
const css = classNames.bind(styles);

const Canvas = ({ imageUrl }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const img = new Image();

    img.onload = function () {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      if (img.naturalWidth >= 2500 || img.naturalHeight >= 2500) {
        canvas.width = img.naturalWidth / 5;
        canvas.height = img.naturalHeight / 5;
      }

      context.drawImage(img, 0, 0, canvas.width, canvas.height);
    };

    img.src = imageUrl;
  }, [imageUrl]);

  return <canvas ref={canvasRef} />;
};

export default function EcleticaStep({
  product,
  onSave,
  onClose,
  isUpdate,
  onSendOrder,
  cartItemIndex,
}) {
  const t = useTranslations();

  const locale = useLocale();

  const combinationId =
    (product?.sizes || [{ sku: null }])[0].sku || product.sku;

  const [localProduct, setLocalProduct] = useState(
    DeepCopy.from({
      ...product,
      quantity: 1,
      selected: true,
    }),
  );

  const [valid, setValid] = useState(false);
  const [questionSelected, setQuestionSelected] = useState(
    localProduct.sizes.length > 1
      ? CHOOSE_SIZE
      : localProduct.items.length > 0
        ? localProduct.items[0]?.id
        : localProduct.extra_ingredients.length > 0
          ? CHOOSE_ADDONS
          : null,
  );

  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [openConfirmSend, setOpenConfirmSend] = useState(false);

  useEffect(() => {
    if (localProduct.sizes.length > 1) {
      setCompletedQuestions([...completedQuestions, CHOOSE_SIZE]);
    }

    if (localProduct.items.length > 0) {
      const completed = localProduct.items.flatMap((parentItem) =>
        parentItem.items.flatMap((childItem) => {
          const isCompleted =
            (parentItem.min > 0 && parentItem.min === childItem.quantity) ||
            (!parentItem.min && childItem.quantity);

          return isCompleted ? [parentItem.id] : [];
        }),
      );

      setCompletedQuestions((completedQuestions) => [
        ...completedQuestions,
        ...completed,
      ]);
    }
  }, [localProduct]);

  useEffect(() => {
    if (questionSelected) {
      const questionEl = document.querySelector(
        `.${styles.question}.${styles.active}`,
      );

      if (!questionEl) return;

      questionEl.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [questionSelected]);

  useEffect(() => {
    if (product.sizes && product.sizes.length > 0) {
      if (localProduct.sizes.filter((s) => s.selected).length == 0) {
        localProduct.sizes[0].selected = true;
      }
    }

    validate();
  }, [localProduct]);

  const validateExtrasSelected = () => {
    // Checa se há questões obrigatórias
    if (localProduct.items?.length > 0) {
      const selectedExtras =
        localProduct.items.filter((q) => {
          //Se não tem minimo, não está incompleto
          if (q.min == 0) {
            return false;
          }

          // Situação onde a etapa é obrigatória e a etapa não tem nenhum item disponível.
          if (q.min > 0 && q.items.length == 0) {
            return true;
          }

          //Se o não tem opções listadas, passa.
          if (q.items?.length == 0) {
            return false;
          }

          const seletedOptions = q.items.filter((o) => o.selected) || [];
          if (seletedOptions[0]?.quantity >= q.min) {
            return false;
          }

          const seletedOptionsQuantity = seletedOptions.reduce(
            (acc, selectedOption) => acc + selectedOption.quantity,
            0,
          );

          if (
            seletedOptionsQuantity < q.min ||
            seletedOptionsQuantity > q.max
          ) {
            return true;
          }

          // Se tem ao menos um item selecionado, não está incompleto
          return !q.items.find((o) => o.selected);
        }).length > 0;

      return selectedExtras;
    }
  };

  const validate = async () => {
    const productSchema = object().shape({ quantity: number().required() });

    setValid(
      (await productSchema.isValid(localProduct)) && !validateExtrasSelected(),
    );
  };

  const onProductChange = (data) => {
    setLocalProduct({ ...localProduct, ...data });
    validate();
  };

  const save = () => {
    onSave && onSave(localProduct);
  };

  const calculateItemPrice = (item) => {
    const props = ["items", "sizes", "extra_ingredients", "doughs", "edges"];

    let total =
      Math.round((item.price || 0) * 100) *
      (item.selected !== false && item.quantity > 0 ? 1 : 0);

    for (const key of props) {
      if (item[key] && item[key].length > 0) {
        for (const subItem of item[key]) {
          total += calculateItemPrice(subItem) * 100;
        }
      }
    }

    return +OMath.div(OMath.times(total, item.quantity), 100).toFixed(2);
  };

  const renderPictureSection = () => {
    return (
      <div className={styles.picture}>
        <div className={styles.close} onClick={() => onClose && onClose()}>
          <ArrowDown />
        </div>
        {localProduct.image && (
          <Canvas
            imageUrl={`${process.env.NEXT_PUBLIC_CDN_ASSETS}/${localProduct.image}`}
          />
        )}
      </div>
    );
  };

  const renderQuantitySection = () => {
    return (
      <div className={styles.quantityContainer}>
        <span
          className={`${styles.minus} ${
            localProduct.quantity <= 1 ? styles.disabled : styles.enabled
          }`}
          onClick={() =>
            localProduct.quantity > 1 &&
            onProductChange({ quantity: --localProduct.quantity })
          }
        >
          <Minus />
        </span>
        <p className={styles.qty}>{localProduct.quantity}</p>
        <span
          className={`${styles.plus} ${styles.enabled}`}
          onClick={() => onProductChange({ quantity: ++localProduct.quantity })}
        >
          <Plus />
        </span>
      </div>
    );
  };

  const renderSubQuestionSection = (option, questionIndexBase, onChange) => {
    return option?.items?.map((subQuestion, subQuestionIndex) => {
      return (
        subQuestion?.items?.length > 0 && (
          <QuestionItemEcletica
            key={subQuestionIndex}
            questionIndex={questionIndexBase.index++}
            question={subQuestion}
            onChange={(q, questionIndex) => {
              localProduct.items[questionIndex] = q;
              onChange({ items: localProduct.items });
            }}
            onChangeSelectedQuestion={(question) => {
              const index = localProduct.items.findIndex(
                (l) => l.id === question,
              );
              let nextQuestion = localProduct.items[index + 1];

              if (index < localProduct.items.length - 1)
                setQuestionSelected(nextQuestion.id);
              else if (hasAddons) setQuestionSelected(CHOOSE_ADDONS);

              if (!completedQuestions.includes(question)) {
                setCompletedQuestions([...completedQuestions, question]);
              }
            }}
          />
        )
      );
    });
  };

  const renderQuestionSection = () => {
    const qObj = { index: 0 };
    const hasAddons = localProduct.extra_ingredients.length > 0;

    return localProduct.items?.map((question, questionIndex) => {
      //Encontra as opções selecionadas
      const selectedOptions = Object.entries(question?.items || {}).filter(
        ([_, option]) => option.selected === true,
      );
      return (
        <>
          {question?.items?.length > 0 && (
            <QuestionItemEcletica
              key={questionIndex}
              questionIndex={qObj.index++}
              questionSelected={questionSelected}
              question={question}
              onChange={(question) => {
                localProduct.items[questionIndex] = question;
                onProductChange({ items: localProduct.items });
              }}
              onChangeSelectedQuestion={(question) => {
                const index = localProduct.items.findIndex(
                  (l) => l.id === question,
                );
                let nextQuestion = localProduct.items[index + 1];

                if (index < localProduct.items.length - 1)
                  setQuestionSelected(nextQuestion.id);
                else if (hasAddons) setQuestionSelected(CHOOSE_ADDONS);

                if (!completedQuestions.includes(question)) {
                  setCompletedQuestions([...completedQuestions, question]);
                }
              }}
            />
          )}
          {
            //Para cada opção selecionada, renderiza suas questões
            selectedOptions.map(([oIndex, option]) =>
              renderSubQuestionSection(
                option,
                qObj,
                (subQuestion, subQuestionIndex) => {
                  //Atualiza a questão da opção renderizada
                  option.items[subQuestionIndex] = subQuestion;
                  //Atualiza a opção da questão renderizada
                  question.items[oIndex] = option;
                  //Atualiza o produto com a questão corrente
                  localProduct.items[questionIndex] = question;
                  onProductChange({ items: localProduct.items });
                },
              ),
            )
          }
        </>
      );
    });
  };

  const renderSizesSection = () => {
    const selectedSize = localProduct.sizes.find((size) => size.selected);
    const isPromotionalPrice = selectedSize.promo_price;

    return (
      <>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>{t("chooseSize")}</p>
          <span className={styles.requiredField}>{t("required")}</span>
        </div>

        <div className={styles.sizes}>
          <span className={styles.sizePrice}>
            {isPromotionalPrice && (
              <>
                <strike className={styles.promoPrice}>
                  {Currency.formatCurrency(selectedSize.old_price)}
                </strike>
                &nbsp;&nbsp;
              </>
            )}
            {Currency.formatCurrency(selectedSize.price)}
          </span>

          {localProduct.sizes.map((size, sizeIndex) => (
            <div
              key={sizeIndex}
              className={`${styles.size} ${
                size.selected ? styles.selected : ""
              }`}
              onClick={() => {
                const hasItems = localProduct.items.filter(
                  (b) => b.items.length > 0,
                );
                const hasAddons = localProduct.extra_ingredients.length > 0;

                localProduct.sizes.forEach((s) => (s.selected = false));
                localProduct.sizes[sizeIndex].selected = true;
                onProductChange({ sizes: localProduct.sizes });

                if (!completedQuestions.includes(CHOOSE_SIZE)) {
                  setCompletedQuestions([...completedQuestions, CHOOSE_SIZE]);
                }

                setQuestionSelected(
                  hasItems
                    ? hasItems[0].id
                    : hasAddons
                      ? CHOOSE_ADDONS
                      : CHOOSE_SIZE,
                );
              }}
            >
              <p className={styles.name}>
                {CatalogHelpers.getTranslation({
                  item: size,
                  field: "name",
                  locale,
                })}
              </p>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderObservationSection = () => {
    return (
      <>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>{t("anyObservations")}</p>
        </div>

        <div className={styles.observationSection}>
          <Textarea
            onChange={(e) => onProductChange({ observation: e.target.value })}
            value={localProduct.observation || ""}
            placeholder={t("exampleRemoveCarrot")}
            className="mt-20"
            max={128}
          />

          <span className={styles.obsLength}>
            {localProduct?.observation?.length || 0}/128
          </span>
        </div>
      </>
    );
  };

  const renderExtrasSection = () => {
    const currentSize = localProduct.sizes.find((s) => s.selected);

    const extras = localProduct.extra_ingredients.map((extra) => {
      const extraSize = extra.sizes.find(
        (size) => size.ref_id === currentSize.ref_id,
      );

      return {
        ...extra,
        price: extraSize?.price || extra.price,
      };
    });

    return (
      <div className="d-flex flex-col">
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>{t("additional")}</p>
        </div>
        <div className={styles.optionItems}>
          {extras.map((e, eIndex) => {
            return (
              <div
                className={styles.optionItem}
                key={eIndex}
                onClick={() => {
                  extras[eIndex].selected = !extras[eIndex].selected;
                  onProductChange({
                    extra_ingredients: extras,
                  });
                }}
              >
                <div className="d-flex flex-col">
                  <p className={styles.optionName}>
                    {CatalogHelpers.getTranslation({
                      item: e,
                      field: "name",
                      locale,
                    })}
                  </p>
                  <div className="d-flex align-center mt-10">
                    <span className={styles.optionPrice}>
                      {`${Currency.formatCurrency(e.price)}`}
                    </span>
                  </div>
                </div>
                <Checkbox
                  value={CatalogHelpers.getTranslation({
                    item: e,
                    field: "name",
                    locale,
                  })}
                  checked={e.selected}
                  key={`extraingredient-${e.id}`}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const elementRef = useRef(null);

  const handleToggleClass = () => {
    if (elementRef.current) {
      elementRef.current.classList.toggle(styles.expand);
    }
  };

  const cart = OrderStorage.getOrder()?.cart;
  const cartHasProducts = cart?.catalog?.items?.length > 0;

  return (
    <>
      <div className={styles.productDetailsContainer}>
        <div className={styles.productDetails}>
          {renderPictureSection()}
          <div className={styles.scrollDetails}>
            <p className={styles.productName}>
              {CatalogHelpers.getTranslation({
                item: product,
                field: "name",
                locale,
              })}
            </p>
            <p
              className={styles.productDescription}
              ref={elementRef}
              onClick={handleToggleClass}
            >
              <TextWithLineBreaks
                text={CatalogHelpers.getTranslation({
                  item: product,
                  field: "description",
                  locale,
                })}
              />
            </p>

            <div className={styles.subtotal}>
              <p>{t("subtotal")}</p>
              <p className={styles.total}>
                {Currency.formatCurrency(calculateItemPrice(localProduct))}
              </p>
            </div>
          </div>
        </div>

        <div className={styles.productContainer}>
          <div className={styles.productChooses}>
            {localProduct.sizes.length > 1 &&
              questionSelected === CHOOSE_SIZE &&
              renderSizesSection()}
            {localProduct.items.length > 0 && renderQuestionSection()}
            {localProduct.extra_ingredients.length > 0 &&
              questionSelected === CHOOSE_ADDONS &&
              renderExtrasSection()}
            {valid && (
              <div className={styles.footer}>
                {renderQuantitySection()}

                {!cartHasProducts ? (
                  <Button
                    design="secondary"
                    className="w-100 mr-10"
                    disabled={!valid}
                    onClick={() => {
                      setOpenConfirmSend(true);
                    }}
                  >
                    Pedir agora
                  </Button>
                ) : null}

                <Button
                  design="primary"
                  className="w-100"
                  disabled={!valid}
                  onClick={() => save()}
                >
                  {isUpdate ? t("update") : t("add")}
                </Button>
              </div>
            )}
            {renderObservationSection()}
          </div>
        </div>
      </div>

      <Drawer
        open={openConfirmSend}
        direction="bottom-center"
        setOpen={() => setOpenConfirmSend(false)}
      >
        <div className={styles.confirmSend}>
          <InfoCircle />

          <p className={styles.title}>Atenção</p>
          <p className={styles.message}>
            Você tem certeza que deseja enviar{" "}
            <strong>{product.quantity} unidade(s) </strong> de{" "}
            <span>"{product.name}"</span> para a cozinha?
          </p>
          <div className={styles.confirmSendFooter}>
            <Button
              design="secondary"
              className="w-100"
              onClick={() => setOpenConfirmSend(false)}
            >
              Cancelar
            </Button>
            <Button
              design="terciary"
              className="w-100"
              onClick={() => {
                setOpenConfirmSend(false);
                if (cartItemIndex !== null)
                  OrderStorage.updateCartItem(cartItemIndex, product);
                else OrderStorage.addItemToCart(product);

                onSendOrder?.();
              }}
            >
              Confirmar
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  );
}
